import { translateErrorToChinese } from '../utils/errorLineAdjust';

const TURTLE_MODULE_CODE = `
import math
import types

turtle_module = types.ModuleType('turtle')

class _SVGCanvas:
    def __init__(self, width=800, height=600):
        self.width = width
        self.height = height
        self.bgcolor = "#ffffff"
        self.elements = []
        self._pen_color = "#000000"
        self._pen_width = 1
        self._fill_color = None
        self._fill_polygon_points = []

    def set_pen_color(self, color):
        self._pen_color = color

    def set_pen_width(self, width):
        self._pen_width = width

    def set_fill_color(self, color):
        self._fill_color = color

    def add_line(self, x1, y1, x2, y2):
        sx1 = x1 + self.width / 2
        sy1 = self.height / 2 - y1
        sx2 = x2 + self.width / 2
        sy2 = self.height / 2 - y2
        self.elements.append(
            f'<line x1="{sx1:.2f}" y1="{sy1:.2f}" x2="{sx2:.2f}" y2="{sy2:.2f}" '
            f'stroke="{self._pen_color}" stroke-width="{self._pen_width}" '
            f'stroke-linecap="round"/>'
        )

    def add_circle(self, cx, cy, r, extent=None, steps=None):
        scx = cx + self.width / 2
        scy = self.height / 2 - cy
        if extent is None or abs(extent) >= 360:
            self.elements.append(
                f'<circle cx="{scx:.2f}" cy="{scy:.2f}" r="{r:.2f}" '
                f'stroke="{self._pen_color}" stroke-width="{self._pen_width}" fill="none"/>'
            )
        else:
            if steps is None:
                steps = max(3, int(abs(extent) / 10))
            angle_step = extent / steps
            points = []
            start_angle = math.radians(0)
            for i in range(steps + 1):
                a = start_angle + math.radians(angle_step * i)
                px = scx + r * math.cos(a)
                py = scy - r * math.sin(a)
                points.append(f"{px:.2f},{py:.2f}")
            self.elements.append(
                f'<polyline points="{" ".join(points)}" '
                f'stroke="{self._pen_color}" stroke-width="{self._pen_width}" '
                f'stroke-linecap="round" fill="none"/>'
            )

    def add_dot(self, cx, cy, size, color):
        scx = cx + self.width / 2
        scy = self.height / 2 - cy
        self.elements.append(
            f'<circle cx="{scx:.2f}" cy="{scy:.2f}" r="{size:.2f}" '
            f'fill="{color}" stroke="none"/>'
        )

    def add_text(self, cx, cy, text, align="left", font_size=12):
        scx = cx + self.width / 2
        scy = self.height / 2 - cy
        anchor = {"left": "start", "center": "middle", "right": "end"}.get(align, "start")
        self.elements.append(
            f'<text x="{scx:.2f}" y="{scy:.2f}" fill="{self._pen_color}" '
            f'font-size="{font_size}" text-anchor="{anchor}" '
            f'dominant-baseline="middle">{text}</text>'
        )

    def begin_fill(self):
        self._fill_polygon_points = []

    def add_fill_point(self, x, y):
        sx = x + self.width / 2
        sy = self.height / 2 - y
        self._fill_polygon_points.append(f"{sx:.2f},{sy:.2f}")

    def end_fill(self):
        if self._fill_polygon_points and self._fill_color:
            pts = " ".join(self._fill_polygon_points)
            self.elements.append(
                f'<polygon points="{pts}" fill="{self._fill_color}" '
                f'stroke="{self._pen_color}" stroke-width="{self._pen_width}"/>'
            )
        self._fill_polygon_points = []
        self._fill_color = None

    def svg(self):
        elements_str = "\\n".join(self.elements)
        return (
            f'<svg xmlns="http://www.w3.org/2000/svg" '
            f'width="{self.width}" height="{self.height}" '
            f'viewBox="0 0 {self.width} {self.height}">'
            f'<rect width="{self.width}" height="{self.height}" fill="{self.bgcolor}"/>'
            f'{elements_str}'
            f'</svg>'
        )

    def clear(self):
        self.elements = []
        self._fill_polygon_points = []


class _TurtleState:
    def __init__(self):
        self.x = 0.0
        self.y = 0.0
        self.heading = 0.0
        self.pen_down = True
        self.visible = True
        self.speed_val = 3
        self.pen_color = "#000000"
        self.fill_color = "#000000"
        self.pen_width = 1
        self.shape_name = "classic"
        self.filling = False
        self.degrees_mode = 360
        self.canvas = _SVGCanvas()

    def reset(self):
        self.x = 0.0
        self.y = 0.0
        self.heading = 0.0
        self.pen_down = True
        self.visible = True
        self.speed_val = 3
        self.pen_color = "#000000"
        self.fill_color = "#000000"
        self.pen_width = 1
        self.shape_name = "classic"
        self.filling = False
        self.degrees_mode = 360
        self.canvas = _SVGCanvas()


_state = _TurtleState()


class Turtle:
    def __init__(self):
        self._st = _state

    def forward(self, distance):
        old_x, old_y = self._st.x, self._st.y
        rad = math.radians(self._st.heading)
        self._st.x += distance * math.cos(rad)
        self._st.y += distance * math.sin(rad)
        if self._st.pen_down:
            self._st.canvas.set_pen_color(self._st.pen_color)
            self._st.canvas.set_pen_width(self._st.pen_width)
            self._st.canvas.add_line(old_x, old_y, self._st.x, self._st.y)
        if self._st.filling:
            self._st.canvas.add_fill_point(self._st.x, self._st.y)

    def backward(self, distance):
        self.forward(-distance)

    def right(self, angle):
        self._st.heading -= angle * (360 / self._st.degrees_mode)
        self._st.heading %= 360

    def left(self, angle):
        self._st.heading += angle * (360 / self._st.degrees_mode)
        self._st.heading %= 360

    def penup(self):
        self._st.pen_down = False

    def pendown(self):
        self._st.pen_down = True

    def goto(self, x, y):
        old_x, old_y = self._st.x, self._st.y
        self._st.x = float(x)
        self._st.y = float(y)
        if self._st.pen_down:
            self._st.canvas.set_pen_color(self._st.pen_color)
            self._st.canvas.set_pen_width(self._st.pen_width)
            self._st.canvas.add_line(old_x, old_y, self._st.x, self._st.y)
        if self._st.filling:
            self._st.canvas.add_fill_point(self._st.x, self._st.y)

    def setx(self, x):
        self.goto(x, self._st.y)

    def sety(self, y):
        self.goto(self._st.x, y)

    def setheading(self, angle):
        self._st.heading = angle * (360 / self._st.degrees_mode)
        self._st.heading %= 360

    def home(self):
        self.goto(0, 0)
        self._st.heading = 0

    def circle(self, radius, extent=None, steps=None):
        if extent is None:
            extent = 360
        extent_deg = extent * (360 / self._st.degrees_mode)
        if radius < 0:
            radius = abs(radius)
            extent_deg = -extent_deg
        if abs(extent_deg) >= 360:
            self._st.canvas.set_pen_color(self._st.pen_color)
            self._st.canvas.set_pen_width(self._st.pen_width)
            self._st.canvas.add_circle(self._st.x, self._st.y, radius)
            rad = math.radians(self._st.heading)
            self._st.x += radius * math.cos(rad)
            self._st.y += radius * math.sin(rad)
            if self._st.filling:
                self._st.canvas.add_fill_point(self._st.x, self._st.y)
        else:
            if steps is None:
                frac = abs(extent_deg) / 360
                steps = max(3, int(frac * max(3, abs(radius) * 0.5)))
            step_len = 2 * math.pi * radius * frac / steps
            step_angle = extent_deg / steps
            old_heading = self._st.heading
            if self._st.filling:
                if not self._st.pen_down:
                    self._st.canvas.add_fill_point(self._st.x, self._st.y)
            for _ in range(steps):
                if self._st.pen_down:
                    self._st.canvas.set_pen_color(self._st.pen_color)
                    self._st.canvas.set_pen_width(self._st.pen_width)
                self.forward(step_len)
                self.left(step_angle * (self._st.degrees_mode / 360))
            self._st.heading = (old_heading + extent_deg) % 360

    def speed(self, s=None):
        if s is None:
            return self._st.speed_val
        self._st.speed_val = max(0, min(10, int(s)))

    def pensize(self, width=None):
        if width is None:
            return self._st.pen_width
        self._st.pen_width = max(1, int(width))

    def pencolor(self, *args):
        if not args:
            return self._st.pen_color
        color = _parse_color(args)
        self._st.pen_color = color
        self._st.canvas.set_pen_color(color)

    def fillcolor(self, *args):
        if not args:
            return self._st.fill_color
        color = _parse_color(args)
        self._st.fill_color = color

    def color(self, *args):
        if not args:
            return (self._st.pen_color, self._st.fill_color)
        if len(args) == 1:
            self.pencolor(args[0])
            self.fillcolor(args[0])
        elif len(args) == 2:
            self.pencolor(args[0])
            self.fillcolor(args[1])
        elif len(args) == 3:
            r, g, b = args
            c = _rgb_to_hex(r, g, b)
            self.pencolor(c)
            self.fillcolor(c)

    def begin_fill(self):
        self._st.filling = True
        self._st.canvas.begin_fill()
        self._st.canvas.set_fill_color(self._st.fill_color)
        self._st.canvas.add_fill_point(self._st.x, self._st.y)

    def end_fill(self):
        self._st.filling = False
        self._st.canvas.end_fill()

    def hideturtle(self):
        self._st.visible = False

    def showturtle(self):
        self._st.visible = True

    def isvisible(self):
        return self._st.visible

    def write(self, text, align="left", font=("Arial", 12, "normal")):
        font_size = font[1] if len(font) > 1 else 12
        self._st.canvas.add_text(self._st.x, self._st.y, str(text), align, font_size)

    def dot(self, size=None, *color_args):
        if size is None:
            size = max(self._st.pen_width + 4, 2 * self._st.pen_width)
        color = self._st.pen_color
        if color_args:
            color = _parse_color(color_args)
        self._st.canvas.add_dot(self._st.x, self._st.y, size / 2, color)

    def stamp(self):
        self._st.canvas.add_dot(self._st.x, self._st.y, 5, self._st.pen_color)

    def shape(self, name=None):
        if name is None:
            return self._st.shape_name
        self._st.shape_name = name

    def position(self):
        return (self._st.x, self._st.y)

    def xcor(self):
        return self._st.x

    def ycor(self):
        return self._st.y

    def heading(self):
        return self._st.heading * (self._st.degrees_mode / 360)

    def distance(self, x, y):
        return math.sqrt((self._st.x - x) ** 2 + (self._st.y - y) ** 2)

    def towards(self, x, y):
        angle = math.degrees(math.atan2(y - self._st.y, x - self._st.x))
        return angle * (self._st.degrees_mode / 360)

    def degrees(self, fullcircle=360):
        self._st.degrees_mode = fullcircle

    def radians(self):
        self._st.degrees_mode = 2 * math.pi

    def reset(self):
        self._st.reset()

    def clear(self):
        self._st.canvas.clear()

    def isdown(self):
        return self._st.pen_down

    def width(self, w=None):
        return self.pensize(w)

    def up(self):
        return self.penup()

    def down(self):
        return self.pendown()

    def fd(self, distance):
        return self.forward(distance)

    def bk(self, distance):
        return self.backward(distance)

    def rt(self, angle):
        return self.right(angle)

    def lt(self, angle):
        return self.left(angle)

    def pu(self):
        return self.penup()

    def pd(self):
        return self.pendown()

    def setpos(self, x, y):
        return self.goto(x, y)

    def setposition(self, x, y):
        return self.goto(x, y)

    def seth(self, angle):
        return self.setheading(angle)

    def st(self):
        return self.showturtle()

    def ht(self):
        return self.hideturtle()


class Screen:
    def __init__(self):
        self._st = _state

    def setup(self, width=800, height=600):
        self._st.canvas.width = width
        self._st.canvas.height = height

    def bgcolor(self, *args):
        if not args:
            return self._st.canvas.bgcolor
        color = _parse_color(args)
        self._st.canvas.bgcolor = color

    def title(self, title_str):
        pass

    def screensize(self, canvwidth=None, canvheight=None, bg=None):
        if canvwidth:
            self._st.canvas.width = canvwidth
        if canvheight:
            self._st.canvas.height = canvheight
        if bg:
            self._st.canvas.bgcolor = _parse_color((bg,))

    def show_scene(self):
        pass

    def tracer(self, n=None, delay=None):
        pass

    def update(self):
        pass

    def mainloop(self):
        pass

    def done(self):
        pass

    def reset(self):
        self._st.reset()

    def clear(self):
        self._st.canvas.clear()

    def window_width(self):
        return self._st.canvas.width

    def window_height(self):
        return self._st.canvas.height


def _rgb_to_hex(r, g, b):
    r = max(0, min(255, int(r * 255)))
    g = max(0, min(255, int(g * 255)))
    b = max(0, min(255, int(b * 255)))
    return f"#{r:02x}{g:02x}{b:02x}"


def _parse_color(args):
    if len(args) == 1:
        a = args[0]
        if isinstance(a, str):
            return a
        if isinstance(a, (tuple, list)):
            return _rgb_to_hex(a[0], a[1], a[2])
    elif len(args) == 3:
        return _rgb_to_hex(args[0], args[1], args[2])
    return "#000000"


def svg():
    return _state.canvas.svg()

def reset():
    _state.reset()

def clear():
    _state.canvas.clear()

def forward(distance):
    Turtle().forward(distance)

def backward(distance):
    Turtle().backward(distance)

def right(angle):
    Turtle().right(angle)

def left(angle):
    Turtle().left(angle)

def penup():
    Turtle().penup()

def pendown():
    Turtle().pendown()

def goto(x, y):
    Turtle().goto(x, y)

def setx(x):
    Turtle().setx(x)

def sety(y):
    Turtle().sety(y)

def setheading(angle):
    Turtle().setheading(angle)

def home():
    Turtle().home()

def circle(radius, extent=None, steps=None):
    Turtle().circle(radius, extent, steps)

def speed(s=None):
    return Turtle().speed(s)

def pensize(width=None):
    return Turtle().pensize(width)

def pencolor(*args):
    return Turtle().pencolor(*args)

def fillcolor(*args):
    return Turtle().fillcolor(*args)

def color(*args):
    return Turtle().color(*args)

def begin_fill():
    Turtle().begin_fill()

def end_fill():
    Turtle().end_fill()

def hideturtle():
    Turtle().hideturtle()

def showturtle():
    Turtle().showturtle()

def write(text, align="left", font=("Arial", 12, "normal")):
    Turtle().write(text, align, font)

def dot(size=None, *color_args):
    Turtle().dot(size, *color_args)

def stamp():
    Turtle().stamp()

def shape(name=None):
    return Turtle().shape(name)

def position():
    return Turtle().position()

def xcor():
    return Turtle().xcor()

def ycor():
    return Turtle().ycor()

def heading():
    return Turtle().heading()

def distance(x, y):
    return Turtle().distance(x, y)

def towards(x, y):
    return Turtle().towards(x, y)

def degrees(fullcircle=360):
    Turtle().degrees(fullcircle)

def radians():
    Turtle().radians()

def isdown():
    return Turtle().isdown()

def width(w=None):
    return Turtle().width(w)

def up():
    Turtle().penup()

def down():
    Turtle().pendown()

def fd(distance):
    Turtle().forward(distance)

def bk(distance):
    Turtle().backward(distance)

def rt(angle):
    Turtle().right(angle)

def lt(angle):
    Turtle().left(angle)

def pu():
    Turtle().penup()

def pd():
    Turtle().pendown()

def setpos(x, y):
    Turtle().goto(x, y)

def setposition(x, y):
    Turtle().goto(x, y)

def seth(angle):
    Turtle().setheading(angle)

def st():
    Turtle().showturtle()

def ht():
    Turtle().hideturtle()

def done():
    pass

def mainloop():
    pass

def bgcolor(*args):
    Screen().bgcolor(*args)

def setup(width=800, height=600):
    Screen().setup(width, height)

def title(title_str):
    Screen().title(title_str)

def screensize(canvwidth=None, canvheight=None, bg=None):
    Screen().screensize(canvwidth, canvheight, bg)

def tracer(n=None, delay=None):
    Screen().tracer(n, delay)

def update():
    Screen().update()

def window_width():
    return Screen().window_width()

def window_height():
    return Screen().window_height()

def TurtleScreen(canvas=None):
    return Screen()

turtle_module.Turtle = Turtle
turtle_module.Screen = Screen
turtle_module._SVGCanvas = _SVGCanvas
turtle_module._TurtleState = _TurtleState
turtle_module._rgb_to_hex = _rgb_to_hex
turtle_module._parse_color = _parse_color
turtle_module._state = _state
turtle_module.svg = svg
turtle_module.reset = reset
turtle_module.clear = clear
turtle_module.forward = forward
turtle_module.backward = backward
turtle_module.right = right
turtle_module.left = left
turtle_module.penup = penup
turtle_module.pendown = pendown
turtle_module.goto = goto
turtle_module.setx = setx
turtle_module.sety = sety
turtle_module.setheading = setheading
turtle_module.home = home
turtle_module.circle = circle
turtle_module.speed = speed
turtle_module.pensize = pensize
turtle_module.pencolor = pencolor
turtle_module.fillcolor = fillcolor
turtle_module.color = color
turtle_module.begin_fill = begin_fill
turtle_module.end_fill = end_fill
turtle_module.hideturtle = hideturtle
turtle_module.showturtle = showturtle
turtle_module.write = write
turtle_module.dot = dot
turtle_module.stamp = stamp
turtle_module.shape = shape
turtle_module.position = position
turtle_module.xcor = xcor
turtle_module.ycor = ycor
turtle_module.heading = heading
turtle_module.distance = distance
turtle_module.towards = towards
turtle_module.degrees = degrees
turtle_module.radians = radians
turtle_module.isdown = isdown
turtle_module.width = width
turtle_module.up = up
turtle_module.down = down
turtle_module.fd = fd
turtle_module.bk = bk
turtle_module.rt = rt
turtle_module.lt = lt
turtle_module.pu = pu
turtle_module.pd = pd
turtle_module.setpos = setpos
turtle_module.setposition = setposition
turtle_module.seth = seth
turtle_module.st = st
turtle_module.ht = ht
turtle_module.done = done
turtle_module.mainloop = mainloop
turtle_module.bgcolor = bgcolor
turtle_module.setup = setup
turtle_module.title = title
turtle_module.screensize = screensize
turtle_module.tracer = tracer
turtle_module.update = update
turtle_module.window_width = window_width
turtle_module.window_height = window_height
turtle_module.TurtleScreen = TurtleScreen

import sys
sys.modules['turtle'] = turtle_module
`;

let turtleInjected = false;

export function hasTurtleImport(code: string): boolean {
  return /\bimport\s+turtle\b/.test(code) || /\bfrom\s+turtle\s+import\b/.test(code);
}

export function stripTurtleImport(code: string): string {
  let result = code;
  result = result.replace(/^from\s+turtle\s+import\s+.+$/gm, '');
  result = result.replace(/^import\s+turtle\s+as\s+(\w+)\s*$/gm, (match, alias) => {
    return `${alias} = turtle`;
  });
  result = result.replace(/^import\s+turtle\b\s*$/gm, '');
  return result;
}

export async function injectTurtleModule(pyodide: any): Promise<void> {
  if (turtleInjected) {
    return;
  }
  await pyodide.runPythonAsync(TURTLE_MODULE_CODE);
  turtleInjected = true;
}

export async function resetTurtleState(pyodide: any): Promise<void> {
  await pyodide.runPythonAsync(`
import turtle
turtle.reset()
turtle.clear()
`);
}

export async function extractTurtleSvg(pyodide: any): Promise<string | null> {
  try {
    const svgResult = await pyodide.runPythonAsync('turtle.svg()');
    if (svgResult && typeof svgResult === 'string' && svgResult.includes('<svg')) {
      return svgResult;
    }
    return null;
  } catch {
    return null;
  }
}

export async function runPythonWithTurtle(
  pyodide: any,
  code: string,
  input: string = ''
): Promise<{ output: string; svg: string | null; error?: string }> {
  try {
    await injectTurtleModule(pyodide);
    await resetTurtleState(pyodide);

    const strippedCode = stripTurtleImport(code);

    let stdout = '';
    pyodide.setStdout({ batched: (text: string) => { stdout += text + '\n'; } });
    pyodide.setStderr({ batched: (text: string) => { stdout += text + '\n'; } });

    if (input) {
      pyodide.globals.set('input_data', input);
      const wrappedCode = `
import sys
from io import StringIO
sys.stdin = StringIO(input_data)
${strippedCode}
`;
      await pyodide.runPythonAsync(wrappedCode);
    } else {
      await pyodide.runPythonAsync(strippedCode);
    }

    const svgResult = await extractTurtleSvg(pyodide);

    return { output: stdout.trim(), svg: svgResult };
  } catch (error: any) {
    const lineOffset = input ? 3 : 0;
    const translatedError = translateErrorToChinese(error.message || '执行失败', lineOffset);
    return { output: '', svg: null, error: translatedError };
  }
}